import {
  LoanPrincipalLateFee as GqlLoanPrincipalLateFee,
  LoanTerminationFee as GqlLoanTerminationFee,
  StopSettings as GqlStopSettings,
} from '@willow/graphql-iso/src/app';
import { LoanPrincipalLateFee, LoanTerminationFee, StopSettings } from '@willow/types-iso';

import { divide, multiply } from './math';

/**
 * Calculate a loan fee equal to a percentage of the remaining principal.
 * @param principal - The remaining principal.
 * @param percentPrincipal - The percent of the principal to charge. This is a number between 0 and 100.
 * @returns The fee amount.
 */
export const calculatePercentFee = ({
  principal,
  percentPrincipal,
}: {
  principal: number;
  percentPrincipal: number;
}): number => multiply(principal, divide(percentPrincipal, 100, 5));

/**
 * Calculate a loan fee equal to a fixed amount per loan or per property.
 * @param fixedAmount - The fixed amount to charge.
 * @param perProperty - Whether to charge the fee per property.
 * @param numProperties - The number of properties to charge the fee for.
 * @returns The fee amount.
 */
export const calculateFixedFee = ({
  fixedAmount,
  perProperty,
  numProperties,
}: {
  fixedAmount: number;
  perProperty: boolean;
  numProperties: number;
}): number => {
  if (perProperty) {
    return multiply(fixedAmount, numProperties);
  }

  return fixedAmount;
};

export const feeDisabledByLoanOverride = (
  loanOverrides:
    | LoanPrincipalLateFee
    | GqlLoanPrincipalLateFee
    | LoanTerminationFee
    | GqlLoanTerminationFee
    | undefined,
) => loanOverrides?.flag === false;

export const feeDisabledByStopSettings = (stopSettings: StopSettings | GqlStopSettings) =>
  stopSettings.omitFromLateFeeAutomation === true;
